import { Component, inject, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { NormalizeUnicodePipe } from '../../pipes/normalize-unicode.pipe';
import { Clipboard } from '@angular/cdk/clipboard';
import { Constants } from '../../../core/constants/constants';
import { DuiNotificationsService } from '../../services/dui-notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { UsersService } from '../../../features/users/users.service';
import { PopoverContainerDirective } from '@progress/kendo-angular-tooltip';
import { StandaloneDefaultImports } from '../../ui/standalone-default-imports';
import { kendoTooltipsDefaultImports } from '../../ui/kendo-default-imports';

type CopyIconKeys = 'nationalId' | 'username' | 'internalId';
export type PopoverValidPositions = 'top' | 'bottom' | 'right' | 'left';

@Component({
    selector: 'avatar',
    imports: [...StandaloneDefaultImports, ...kendoTooltipsDefaultImports],
    providers: [NormalizeUnicodePipe],
    templateUrl: './avatar.component.html',
    styleUrls: ['./_avatar.scss']
})
export class AvatarComponent implements OnChanges, OnInit {
    // Section: SERVICES
    //----------------------------------------------------------------------
    private normalizeUnicodePipe = inject(NormalizeUnicodePipe);
    private translate = inject(TranslateService);
    private router = inject(Router);
    private clipboard = inject(Clipboard);
    private notificationService = inject(DuiNotificationsService);
    private usersService = inject(UsersService);

    // Section: PUBLIC PROPERTIES
    //----------------------------------------------------------------------
    @Input() name!: string;
    @Input() surname!: string;
    @Input() nickname!: string;
    @Input() userId?: number;
    @Input() small?: boolean;
    @Input() medium?: boolean;
    @Input() large?: boolean;
    @Input() xlarge?: boolean;
    @Input() thumbnails?: any;
    @Input() state?: number | null = null;
    @Input() access?: string;
    @Input() title?: string;
    @Input() chatroom?: boolean;
    @Input() anonymous?: boolean;
    @Input() showPopover?: boolean;
    @Input() popoverPosition?: PopoverValidPositions;
    @Input() popoverOnClick?: boolean;
    @Input() shortVersion?: boolean;

    initials!: string;
    avatarBGColor!: string;
    thumbnail!: string;
    moreDetails: boolean = false;
    additionalData: any = null;
    isSyncing: boolean = true;
    isCopyIconVisible: Record<CopyIconKeys, boolean> = {
        nationalId: false,
        username: false,
        internalId: false
    };
    readonly USER_STATES = Constants.USER_STATES;

    // Section: PRIVATE PROPERTIES
    //----------------------------------------------------------------------

    @ViewChild('avatar', { static: false }) private avatar!: PopoverContainerDirective;

    private previousId: number | null | undefined = null;
    private delayPopover: any;
    private readonly DEFAULT_AVATAR_COLOR = '#8C8C8C';
    private readonly NAMES_COLORS_FIRST: string[] = ['#E24D48', '#D73965', '#9036AA', '#6141B1', '#4254AF', '#758DF1', '#4AA8EE', '#54BAD1'];
    private readonly NAMES_COLORS_SECOND: string[] = ['#40B5A4', '#76AE66', '#97C05C', '#D0DA59', '#EBDA57', '#F6C244', '#F19C38', '#ED6337'];

    // Section: HOOKS
    //----------------------------------------------------------------------
    ngOnInit() {
        this.moreDetails = !!this.shortVersion;
    }

    ngOnChanges(changes: any) {
        if (changes.name && changes.surname) {
            if (!!changes.name.currentValue && !!changes.surname.currentValue) {
                this.getUserInitialsAndColor();
            } else if (changes.name.currentValue !== changes.name.previousValue && changes.surname.currentValue !== changes.surname.previousValue) {
                this.initials = '';
                this.avatarBGColor = '';
                this.thumbnail = '';
            }
        } else if (changes.name || changes.surname) {
            this.getUserInitialsAndColor();
        }
        if (changes.thumbnails) {
            this.setThumbnail();
        }
        if (changes.userId) {
            if (changes.userId.currentValue !== changes.userId.previousValue && !changes.userId.firstChange) {
                this.previousId = changes.userId.previousValue;
                this.isSyncing = true;
            }
        }
    }

    // Section: PUBLIC METHODS
    //----------------------------------------------------------------------
    getUserStatusText(): string {
        switch (this.state) {
            case this.USER_STATES.PENDING_ACTIVATION:
                return this.translate.instant('global.userstates.pendingactivation');
            case this.USER_STATES.LOCKED:
                return this.translate.instant('global.userstates.locked');
            case this.USER_STATES.EXPIRED_CREDENTIALS:
                return this.translate.instant('global.userstates.expiredCredentials');
            case this.USER_STATES.REGISTERED:
                if (!this.access) {
                    return this.translate.instant('global.userstates.pendingAccess');
                }
                return '';
            default:
                return '';
        }
    }

    onShowData(event: any, popover: any): void {
        const shouldFetchUserData = !!this.userId && (!this.additionalData || this.userId !== this.previousId);
        if (this.popoverOnClick) {
            if (shouldFetchUserData) {
                this.getUserDataPopover();
            }
        } else {
            event.preventDefault();
            this.delayPopover = setTimeout(() => {
                if (shouldFetchUserData) {
                    this.getUserDataPopover();
                }
                this.avatar.show(popover);
            }, 700);
        }
    }

    onHideData() {
        if (this.delayPopover) {
            clearTimeout(this.delayPopover);
        }
    }

    goToUserProfile(): void {
        if (this.userId) {
            this.router.navigate(['/user', this.userId, 'update', 'personaldata']);
        }
    }

    showCopyIcon(value: CopyIconKeys): void {
        if (!this.shortVersion) {
            this.isCopyIconVisible[value] = true;
        }
    }

    hideCopyIcon(value: CopyIconKeys): void {
        this.isCopyIconVisible[value] = false;
    }

    copyToClipboard(value: string): void {
        const isCopySuccessful = this.clipboard.copy(value);
        if (isCopySuccessful) {
            this.notificationService.showSuccessNotification(this.translate.instant('500.copiedToClipboard'));
        }
    }

    onImageError(event: Event): void {
        const target = event.target as HTMLImageElement;
        target.onerror = null;
        target.src = '/assets/img/image-not-found.png';
    }

    // Section: PRIVATE METHODS
    //----------------------------------------------------------------------

    private getUserDataPopover(): void {
        this.usersService.getPopoverData(this.userId).subscribe((response) => {
            this.previousId = this.userId;
            this.isSyncing = false;
            this.additionalData = response;
            this.additionalData.showIdentificationGroup = !!response.nationalIdNumber || !!response.companyInternalId || response.companyPosition;
            this.additionalData.showResponsibleGroup = !!response.team?.responsible;
            if (!!response.team?.responsible) {
                this.additionalData.team.responsible.thumbnail =
                    response.team.responsible.avatarThumbnailMUrl || response.team.responsible.avatarThumbnailSUrl || response.team.responsible.avatarUrl;
                if (!this.additionalData.team.responsible.thumbnail) {
                    this.additionalData.team.responsible.initials =
                        (response.team.responsible.nickname?.charAt(0) || response.team.responsible.name.charAt(0)) +
                        response.team.responsible.surname.charAt(0);
                    this.additionalData.team.responsible.avatarBGColor = this.getAvatarBGColor(
                        response.team.responsible.name.charAt(0),
                        response.team.responsible.surname.charAt(0)
                    );
                }
            }
        });
    }

    private getUserInitialsAndColor() {
        const nameInitial = this.nickname?.charAt(0) || this.name.charAt(0);
        const surnameInitial = this.surname.charAt(0);
        this.initials = nameInitial + surnameInitial;
        this.avatarBGColor = this.getAvatarBGColor(nameInitial, surnameInitial);

        // this.setThumbnail();
    }

    private setThumbnail(): void {
        if (!!this.small) {
            this.thumbnail = this.thumbnails?.thumbnail_S || this.thumbnails?.thumbnail_M;
        } else {
            // If large or if size is not specified
            this.thumbnail = this.thumbnails?.thumbnail_M || this.thumbnails?.thumbnail_S;
        }
    }

    private getLetterColor(initial: string, colorsArray: Array<string>): string {
        const normalizedInitial = this.normalizeUnicodePipe.transform(initial);

        switch (true) {
            case normalizedInitial >= 'A' && normalizedInitial <= 'C':
                return colorsArray[0];
            case normalizedInitial >= 'D' && normalizedInitial <= 'F':
                return colorsArray[1];
            case normalizedInitial >= 'G' && normalizedInitial <= 'I':
                return colorsArray[2];
            case normalizedInitial >= 'J' && normalizedInitial <= 'L':
                return colorsArray[3];
            case normalizedInitial === 'M' || normalizedInitial === 'N' || normalizedInitial === 'Ñ':
                return colorsArray[4];
            case normalizedInitial >= 'O' && normalizedInitial <= 'Q':
                return colorsArray[5];
            case normalizedInitial >= 'R' && normalizedInitial <= 'T':
                return colorsArray[6];
            case normalizedInitial >= 'U' && normalizedInitial <= 'Z':
                return colorsArray[7];
            default:
                return this.DEFAULT_AVATAR_COLOR;
        }
    }

    private getAvatarBGColor(nameInitial: string, surnameInitial: string): string {
        const normalizedNameInitial = this.normalizeUnicodePipe.transform(nameInitial);

        const selectedColors = normalizedNameInitial >= 'A' && normalizedNameInitial <= 'M' ? this.NAMES_COLORS_FIRST : this.NAMES_COLORS_SECOND;

        return this.getLetterColor(surnameInitial, selectedColors);
    }
}
